import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAnalogClock from "../hooks/useAnalogClock";
import useCamera from "../hooks/useCamera";
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCameraRotate, faSpinner, faExclamationTriangle, faCheckCircle, faLocationDot, faUser, faClock, faArrowLeft, faCamera } from '@fortawesome/free-solid-svg-icons';

function Exit() {
  const navigate = useNavigate();
  const userid = localStorage.getItem("user_id");

  // 📦 Custom hooks
  const { hourRef, minuteRef, secondRef, currentTime } = useAnalogClock();
  const { videoRef, canvasRef, flipCamera, captureImage, stopCamera, cameraError, isCameraFacingUser, startCamera } = useCamera();
  const [employelist, setemployelist] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [selectedValue, setSelectedValue] = useState("");
  const [selectedid, setselectedid] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState({
    employees: true,
    location: true,
    camera: true,
    submitting: false
  });
  const [cameraActive, setCameraActive] = useState(true);

  const handleselect = (e) => {
    const selectedIndex = e.target.selectedIndex;
    const employename = e.target.options?.[selectedIndex]?.text;
    const employeeid = e.target.value;
    
    if (employename && employeeid) {
      setSelectedValue(employename);
      setselectedid(employeeid);
      setError(null);
    } else {
      setSelectedValue("");
      setselectedid("");
    }
  }

  const handleGoBack = () => {
    stopCamera();
    navigate(-1); // Go back to previous page
  };

  useEffect(() => {
    fetchemploylist();
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      setLoading(prev => ({ ...prev, location: false }));
      return;
    }

    const geoTimeout = setTimeout(() => {
      setError("Location detection is taking longer than expected. Please check your location permissions.");
      setLoading(prev => ({ ...prev, location: false }));
    }, 10000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(geoTimeout);
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setError(null);
        setLoading(prev => ({ ...prev, location: false }));
      },
      (err) => {
        clearTimeout(geoTimeout);
        let errorMessage = "Permission denied or location unavailable";
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = "Location access denied. Please enable location permissions to continue.";
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case err.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
          default:
            errorMessage = "An unknown error occurred while getting location.";
        }
        setError(errorMessage);
        setLoading(prev => ({ ...prev, location: false }));
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  }, []);

  // Listen for camera initialization
  useEffect(() => {
    if (!cameraError && videoRef.current) {
      const checkCameraActive = setInterval(() => {
        if (videoRef.current && videoRef.current.readyState === 4) {
          setLoading(prev => ({ ...prev, camera: false }));
          clearInterval(checkCameraActive);
        }
      }, 500);
      
      return () => clearInterval(checkCameraActive);
    } else if (cameraError) {
      setLoading(prev => ({ ...prev, camera: false }));
    }
  }, [cameraError, videoRef]);

  // Convert base64 to Blob
  function dataURLtoBlob(dataURL) {
    if (!dataURL || typeof dataURL !== 'string' || !dataURL.includes(',')) {
      console.error("Invalid dataURL:", dataURL);
      return null;
    }

    try {
      const parts = dataURL.split(',');
      const mimeMatch = parts[0].match(/:(.*?);/);

      if (!mimeMatch || !mimeMatch[1]) {
        console.error("Invalid MIME type in dataURL:", parts[0]);
        return null;
      }

      const mime = mimeMatch[1];
      const bstr = atob(parts[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch (error) {
      console.error("Error converting dataURL to Blob:", error);
      return null;
    }
  }

  const fetchemploylist = async () => {
    try {
      setError(null);
      const response = await axios.get(`https://ims-attendanceback.indevconsultancy.in/api/employee-list?kitchen_id=${userid}`);
      if (response.data && response.data.employeelist) {
        setemployelist(response.data.employeelist);
      } else {
        setError("No employees found for this kitchen");
      }
    } catch (error) {
      console.log("Error fetching employee list:", error);
      let errorMessage = "Failed to load employee list";
      if (error.response) {
        errorMessage = `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }
      setError(errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, employees: false }));
    }
  };

  const handlePunch = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setLoading(prev => ({ ...prev, submitting: true }));
    setError(null);
    setSuccess(null);

    // Validate inputs
    if (!selectedid) {
      setError("कृपया कर्मचारी चुनें");
      setIsSubmitting(false);
      setLoading(prev => ({ ...prev, submitting: false }));
      return;
    }

    if (!location.latitude || !location.longitude) {
      setError("Location not available. Please enable location services.");
      setIsSubmitting(false);
      setLoading(prev => ({ ...prev, submitting: false }));
      return;
    }

    const image = captureImage();
    if (!image) {
      setError("छवि कैप्चर नहीं हो सकी. कैमरा की अनुमति सुनिश्चित करें");
      setIsSubmitting(false);
      setLoading(prev => ({ ...prev, submitting: false }));
      return;
    }

    try {
      // Get current time in Kolkata timezone
      const dateInKolkata = new Date().toLocaleString("en-US", {
        timeZone: "Asia/Kolkata"
      });

      const date = new Date(dateInKolkata);
      const pad = (n) => n.toString().padStart(2, "0");
      const formattedPunchTime = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

      const Imagefile = dataURLtoBlob(image); // convert base64 to Blob

      if (!Imagefile) {
        setError("Image processing failed. Please try again.");
        setIsSubmitting(false);
        setLoading(prev => ({ ...prev, submitting: false }));
        return;
      }

      // Store data in localStorage
      localStorage.setItem("exitImage", image);
      localStorage.setItem("exitTime", formattedPunchTime);
      localStorage.setItem("exitName", selectedValue);
      localStorage.setItem("latitude_exit", location.latitude);
      localStorage.setItem("longitudeexit", location.longitude);
      localStorage.setItem("employee_id", selectedid);

      // Prepare form data for API
      const formData = new FormData();
      formData.append("user_id", selectedid);
      formData.append("exit_time", formattedPunchTime);
      formData.append("latitude_exit", location.latitude);
      formData.append("longitudeexit", location.longitude);
      formData.append("filename", Imagefile, `capture_${Date.now()}.jpg`);

      const res = await axios.post(
        "https://ims-attendanceback.indevconsultancy.in/api/exit-submit/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000, // 30 second timeout
        }
      );

      if (res.data.status === true) {
        setSuccess("उपस्थिति सफलतापूर्वक दर्ज की गई");
        setTimeout(() => {
          stopCamera();
          navigate("/exit-slip");
        }, 1500);
      } else {
        setError(res.data.message || "उपस्थिति दर्ज नहीं हो पाई");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      let errorMessage = "नेटवर्क त्रुटि!";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please check your internet connection.";
      } else if (error.response) {
        errorMessage = `Server error: ${error.response.status}`;
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  const retryCamera = async () => {
    setLoading(prev => ({ ...prev, camera: true }));
    setError(null);
    try {
      await startCamera();
      setLoading(prev => ({ ...prev, camera: false }));
    } catch (err) {
      setError("Failed to start camera. Please check permissions.");
      setLoading(prev => ({ ...prev, camera: false }));
    }
  };

  const toggleCamera = () => {
    flipCamera();
  };

  // Check if all initial loading is complete
  const isLoading = loading.employees || loading.location || loading.camera;

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-cream py-4">
      <div className="container my-3" style={{ maxWidth: "600px" }}>
        <div className="card shadow text-center overflow-hidden">
          <div className="card-header fs-3 fw-semibold bg-skyBlue text-white py-3 position-relative">
            <button 
              className="btn btn-sm btn-light position-absolute start-0 top-50 translate-middle-y ms-3"
              onClick={handleGoBack}
              style={{ borderRadius: "50%", width: "40px", height: "40px" }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h4 className="mb-0">प्रस्थान पंचिंग</h4>
          </div>

          <div className="card-body bg-pink p-4 position-relative">
            {/* Global Loader */}
            {isLoading && (
              <div className="loader-overlay">
                <div className="loader-content">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-3">
                    <p className="mb-1">Loading components...</p>
                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                      {loading.employees && (
                        <span className="badge bg-secondary">
                          <FontAwesomeIcon icon={faUser} className="me-1" /> Employees
                        </span>
                      )}
                      {loading.location && (
                        <span className="badge bg-secondary">
                          <FontAwesomeIcon icon={faLocationDot} className="me-1" /> Location
                        </span>
                      )}
                      {loading.camera && (
                        <span className="badge bg-secondary">
                          <FontAwesomeIcon icon={faCamera} className="me-1" /> Camera
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 flex-shrink-0" />
                <div className="flex-grow-1">{error}</div>
                {(error.includes("camera") || error.includes("Camera")) && (
                  <button className="btn btn-sm btn-outline-danger ms-3" onClick={retryCamera}>
                    Retry
                  </button>
                )}
              </div>
            )}
            
            {/* Success Alert */}
            {success && (
              <div className="alert alert-success d-flex align-items-center" role="alert">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                <div>{success}</div>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="employeeSelect" className="form-label fw-medium mb-2">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                कर्मचारी का नाम चुनें
              </label>
              <div className="position-relative">
                <select
                  id="employeeSelect"
                  className="form-select w-75 mx-auto border-primary"
                  value={selectedid}
                  onChange={handleselect}
                  disabled={isSubmitting || loading.employees}
                >
                  <option value="">नाम चुनें</option>
                  {employelist.map((emp) => (
                    <option key={emp.empdetailsid} value={emp.empdetailsid}>
                      {emp.emp_name}
                    </option>
                  ))}
                </select>
                {loading.employees && (
                  <div className="position-absolute top-50 end-0 translate-middle-y me-5">
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin text-secondary" />
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4 position-relative">
              <div className="d-flex justify-content-center align-items-center mb-3">
                <FontAwesomeIcon icon={faCamera} className="me-2 text-magenta" />
                <span className="fw-semibold">कैमरा</span>
              </div>
              
              {cameraError ? (
                <div className="d-flex flex-column align-items-center">
                  <div className="rounded-circle bg-light d-flex align-items-center justify-content-center shadow"
                    style={{
                      width: "200px",
                      height: "200px",
                      border: "3px dashed #c2185b",
                    }}>
                    <div className="text-center text-muted">
                      <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                      <p className="mt-2 mb-0">Camera Unavailable</p>
                    </div>
                  </div>
                  <button className="btn btn-outline-primary mt-3" onClick={retryCamera}>
                    <FontAwesomeIcon icon={faCameraRotate} className="me-2" />
                    Enable Camera
                  </button>
                </div>
              ) : (
                <>
                  {loading.camera && (
                    <div className="position-absolute top-50 start-50 translate-middle z-10">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading camera...</span>
                      </div>
                    </div>
                  )}
                  <div className="position-relative d-inline-block">
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="rounded-circle shadow"
                      style={{
                        width: "200px",
                        height: "200px",
                        objectFit: "cover",
                        border: "3px solid #c2185b",
                        transform: isCameraFacingUser ? "scaleX(-1)" : "scaleX(1)",
                        opacity: loading.camera ? 0.5 : 1
                      }}
                    />
                    <button
                      className="btn bg-magenta text-white btn-sm fw-semibold position-absolute bottom-0 end-0 translate-middle"
                      onClick={toggleCamera}
                      disabled={isSubmitting || loading.camera}
                      title="Flip Camera"
                      style={{ borderRadius: "50%", width: "36px", height: "36px" }}
                    >
                      <FontAwesomeIcon icon={faCameraRotate} />
                    </button>
                  </div>
                </>
              )}
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>

            <div className="d-flex justify-content-center align-items-center gap-4 mt-4 flex-wrap">
              <div className="text-center position-relative">
                {loading.location && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light bg-opacity-75 rounded">
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin text-primary" />
                  </div>
                )}
                <div className="mb-4 d-flex justify-content-center">
                  <div className="clock-wrapper position-relative">
                    <div className="clock-face">
                      <div ref={hourRef} className="hand hour-hand"></div>
                      <div ref={minuteRef} className="hand minute-hand"></div>
                      <div ref={secondRef} className="hand second-hand"></div>
                    </div>
                    <div className="position-absolute top-50 start-50 translate-middle bg-dark rounded-circle"
                      style={{ width: "10px", height: "10px" }}></div>
                  </div>
                </div>
                <div className="fw-semibold fs-5 text-dark">
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  {currentTime}
                </div>
                {location.latitude && (
                  <div className="small text-muted mt-1">
                    <FontAwesomeIcon icon={faLocationDot} className="me-1" />
                    Location: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                  </div>
                )}
              </div>

              <button
                className="btn btn-primary px-4 py-2 fw-semibold d-flex align-items-center justify-content-center"
                onClick={handlePunch}
                disabled={isSubmitting || !selectedid || cameraError || isLoading}
                style={{ minWidth: "120px" }}
              >
                {loading.submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="me-2 fa-spin" />
                    प्रोसेसिंग...
                  </>
                ) : (
                  "पंच करें"
                )}
              </button>
            </div>
          </div>

          <div className="card-footer text-muted small py-2 bg-light">तकनीकी साझेदार: Indev Consultancy ©2025</div>
        </div>

        <style>{`
          .bg-cream {
            background-color: #f8f9fa;
          }
          .bg-skyBlue {
            background: linear-gradient(135deg, #0277bd, #01579b);
          }
          .bg-pink {
            background-color: #fce4ec;
          }
          .bg-magenta {
            background-color: #c2185b;
            border-color: #c2185b;
          }
          .bg-magenta:hover {
            background-color: #ad1457;
            border-color: #ad1457;
          }
          .clock-wrapper {
            width: 120px;
            height: 120px;
            border: 4px solid #0277bd;
            border-radius: 50%;
            position: relative;
            background: white;
            box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.1);
          }
          .clock-face {
            width: 100%;
            height: 100%;
            position: relative;
          }
          .hand {
            position: absolute;
            bottom: 50%;
            left: 50%;
            transform-origin: bottom center;
            transform: rotate(90deg);
            transition: transform 0.05s cubic-bezier(0.4, 2.3, 0.3, 1);
          }
          .hour-hand {
            width: 5px;
            height: 30px;
            background: #000;
            z-index: 3;
            border-radius: 2px;
          }
          .minute-hand {
            width: 3px;
            height: 45px;
            background: #d32f2f;
            z-index: 2;
            border-radius: 2px;
          }
          .second-hand {
            width: 2px;
            height: 55px;
            background: #ff8f00;
            z-index: 1;
            border-radius: 1px;
          }
          .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .loader-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(255, 255, 255, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            border-radius: 8px;
          }
          .loader-content {
            text-align: center;
            padding: 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
          }
        `}</style>
      </div>
    </div>
  );
}

export default Exit;